/**
 * 캐시된 설비 문서(점검 데이터) 조회 훅
 *
 * 온라인: GraphQL 쿼리 → 결과를 IndexedDB에 캐시
 * 오프라인: IndexedDB에서 캐시된 데이터 조회
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  MACHINE_DOCUMENT_QUERY,
  type MachineDocument,
  type ProjectInfo,
  type MachineDocumentQueryData,
} from "@/lib/graphql/equipment";
import { getCachedMachine, getCachedMachinesByDocKey, cacheMachine } from "@/lib/db/machines";
import { getCachedProject } from "@/lib/db/projects";
import { useNetworkStatus } from "./useNetworkStatus";
import type { CachedMachine } from "@/lib/db/types";
import type { CheckResultValueNullable } from "@/types";

interface UseCachedMachineDocumentParams {
  projectId: string;
  docKey: string;
  machineId?: string;
}

interface UseCachedMachineDocumentResult {
  project: ProjectInfo | null;
  machineDocument: MachineDocument | null;
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  refetch: () => void;
}

export function useCachedMachineDocument({
  projectId,
  docKey,
  machineId,
}: UseCachedMachineDocumentParams): UseCachedMachineDocumentResult {
  const { isOffline, isLoading: networkLoading } = useNetworkStatus();
  const [cachedData, setCachedData] = useState<{
    project: ProjectInfo | null;
    machineDocument: MachineDocument | null;
  } | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(true);
  // 캐시 로드 완료 여부 (machineId 변경 시 리셋됨)
  const [cacheLoadedOnce, setCacheLoadedOnce] = useState(false);

  // machineId 변경 시 캐시 로드 상태 리셋
  useEffect(() => {
    setCacheLoadedOnce(false);
  }, [projectId, docKey, machineId]);

  // 캐시에서 먼저 로드 (항상 실행)
  useEffect(() => {
    async function loadCacheFirst() {
      if (!projectId || !docKey || cacheLoadedOnce) {
        setCacheLoading(false);
        return;
      }

      setCacheLoading(true);
      try {
        let cached: CachedMachine | undefined;

        if (machineId) {
          // machineId가 있으면 정확한 키로 조회
          cached = await getCachedMachine(projectId, docKey, machineId);
        } else {
          // machineId가 없으면 해당 그룹의 첫 번째 설비 조회
          const machinesInGroup = await getCachedMachinesByDocKey(projectId, docKey);
          if (machinesInGroup.length > 0) {
            cached = machinesInGroup[0];
          }
        }

        if (cached) {
          // 캐시된 점검항목을 GraphQL 응답 형식으로 매핑
          const mapCheckListItem = (item: {
            _id?: string;
            item: string;
            result?: string | null;
            category?: string;
            image?: string;
            imageO?: string;
            imageX?: string;
            underReason?: string;
            underSolution?: string;
          }) => ({
            id: item._id || "",
            title: item.item,
            result: item.result || undefined,
            image: item.image,
            imageO: item.imageO,
            imageX: item.imageX,
            underReason: item.underReason,
            underSolution: item.underSolution,
            group: item.category,
          });

          // 레거시 데이터 처리: MAINTENANCE 프로젝트인데 maintenance가 비어있고 checkList에 데이터가 있는 경우
          // 또는 그 반대의 경우를 처리 (서버에서 항상 checkList에 저장했던 구버전 데이터)
          const hasCheckListData = (cached.checkList?.length ?? 0) > 0;
          const hasMaintenanceData = (cached.maintenance?.length ?? 0) > 0;

          // 프로젝트 타입 확인 (캐시 또는 fallback)
          let cachedProjectType = cached.project?.type;
          if (!cachedProjectType) {
            const projectCache = await getCachedProject(projectId);
            cachedProjectType = projectCache?.type;
          }

          // 레거시 데이터 매핑: 실제 데이터가 있는 필드를 사용
          const rawCheckList = cached.checkList?.map(mapCheckListItem) ?? [];
          const rawMaintenance = cached.maintenance?.map(mapCheckListItem) ?? [];

          let mappedCheckList = rawCheckList;
          let mappedMaintenance = rawMaintenance;

          // MAINTENANCE 프로젝트인데 maintenance가 비어있고 checkList에 데이터가 있으면 복사
          if (cachedProjectType === "MAINTENANCE" && !hasMaintenanceData && hasCheckListData) {
            mappedMaintenance = rawCheckList;
          }
          // PERFORMANCE_CHECK 프로젝트인데 checkList가 비어있고 maintenance에 데이터가 있으면 복사
          else if (cachedProjectType !== "MAINTENANCE" && !hasCheckListData && hasMaintenanceData) {
            mappedCheckList = rawMaintenance;
          }

          const machineDoc: MachineDocument = {
            id: cached.machine._id,
            title: cached.machine.name,
            position: undefined,
            modelNo: cached.machine.model,
            modelYear: undefined,
            modelCreator: cached.machine.manufacturer,
            checkList: mappedCheckList,
            maintenance: mappedMaintenance,
            data: cached.dataList?.map((item) => ({
              id: item._id || "",
              section: item.category,
              title: item.item,
              unit: item.unit,
              designValue: undefined,
              checkValue: item.value,
            })),
            pictures: cached.pictures?.map((p) => p.url) || [],
            version: cached.serverVersion || 1,
          };

          // 캐시된 프로젝트 정보 로드 (타입 정보 포함)
          // 설비 문서 캐시에 project 정보가 없으면 프로젝트 캐시에서 가져오기
          let cachedProject: ProjectInfo | null = null;
          if (cached.project) {
            cachedProject = {
              _id: cached.project.id,
              name: cached.project.name,
              type: cached.project.type,
            };
          } else {
            // 이전 버전 캐시: 프로젝트 캐시에서 타입 정보 가져오기
            const projectCache = await getCachedProject(projectId);
            if (projectCache?.type) {
              cachedProject = {
                _id: projectCache._id,
                name: projectCache.name,
                type: projectCache.type,
              };
            }
          }

          setCachedData({
            project: cachedProject,
            machineDocument: machineDoc,
          });
          setIsFromCache(true);
        }
        setCacheLoadedOnce(true);
      } catch (err) {
        console.error("[useCachedMachineDocument] 캐시 로드 실패:", err);
      } finally {
        setCacheLoading(false);
      }
    }
    loadCacheFirst();
  }, [projectId, docKey, machineId, cacheLoadedOnce]);

  // GraphQL 쿼리 (네트워크 상태 확인 완료 후, 온라인 시에만 실행)
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<MachineDocumentQueryData>(MACHINE_DOCUMENT_QUERY, {
    variables: { projectId, docKey, machineId },
    skip: !projectId || !docKey || networkLoading || isOffline,
    fetchPolicy: "network-only",
  });

  // 온라인 데이터 수신 시 캐시에 저장
  useEffect(() => {
    async function saveToCache() {
      if (!isOffline && data?.machineDocument && projectId && docKey) {
        try {
          const doc = data.machineDocument;
          // 캐시 키는 서버에서 받은 실제 doc.id 사용
          // useProjectSync와 동일한 키 구조로 저장해야 오프라인에서 찾을 수 있음
          const actualMachineId = doc.id;

          // 기존 캐시에서 measureDiscus 보존 (#469)
          const existing = await getCachedMachine(projectId, docKey, actualMachineId);

          const machineToCache: CachedMachine = {
            _id: `${projectId}-${docKey}-${actualMachineId}`,
            projectId,
            // 프로젝트 정보 캐시 (타입 정보 포함 - 오프라인에서 checkList/maintenance 구분에 필요)
            project: data.project
              ? {
                  id: data.project._id,
                  name: data.project.name,
                  type: data.project.type,
                  measureDiscus: existing?.project?.measureDiscus,
                }
              : undefined,
            group: {
              _id: docKey,
              docKey,
              docLabel: docKey,
            },
            machine: {
              _id: doc.id,
              machineId: doc.id,
              name: doc.title,
              model: doc.modelNo,
              manufacturer: doc.modelCreator,
            },
            checkList:
              doc.checkList?.map((item) => ({
                _id: item.id,
                category: item.group || "",
                item: item.title,
                result: (item.result || null) as CheckResultValueNullable,
                image: item.image,
                imageO: item.imageO,
                imageX: item.imageX,
                underReason: item.underReason,
                underSolution: item.underSolution,
              })) || [],
            maintenance:
              doc.maintenance?.map((item) => ({
                _id: item.id,
                category: item.group || "",
                item: item.title,
                result: (item.result || null) as CheckResultValueNullable,
                image: item.image,
                imageO: item.imageO,
                imageX: item.imageX,
                underReason: item.underReason,
                underSolution: item.underSolution,
              })) || [],
            dataList:
              doc.data?.map((item) => ({
                _id: item.id,
                category: item.section,
                item: item.title,
                value: item.checkValue,
                unit: item.unit,
              })) || [],
            pictures:
              doc.pictures?.map((url, idx) => ({
                _id: `${actualMachineId}-pic-${idx}`,
                url,
                createdAt: new Date().toISOString(),
              })) || [],
            synced: true,
            syncStatus: "synced",
            cachedAt: Date.now(),
            serverVersion: doc.version,
          };

          await cacheMachine(machineToCache);
          setCachedData({
            project: data.project,
            machineDocument: doc,
          });
          setIsFromCache(false);
        } catch (err) {
          console.error("[useCachedMachineDocument] 캐시 저장 실패:", err);
        }
      }
    }

    saveToCache();
  }, [isOffline, data, projectId, docKey, machineId]);

  const handleRefetch = useCallback(() => {
    if (!isOffline) {
      refetch();
    }
  }, [isOffline, refetch]);

  // 결과 결정: 서버 데이터가 있으면 그것 사용, 없으면 캐시
  const project = data?.project || cachedData?.project || null;
  const machineDocument = data?.machineDocument || cachedData?.machineDocument || null;

  // 로딩: 캐시 로딩 중이거나 (온라인이고 쿼리 로딩 중이면서 캐시가 없을 때)
  const loading = cacheLoading || (!isOffline && queryLoading && !cachedData);
  const error = isOffline ? null : (queryError ?? null);

  return {
    project,
    machineDocument,
    loading,
    error,
    isFromCache,
    refetch: handleRefetch,
  };
}
